import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import type { ApiResponse, PaginatedResponse } from '@/types'
import { apiConfig } from '../config/api'

class ApiClient {
  private client: AxiosInstance

  constructor(baseURL: string = apiConfig.baseURL) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: apiConfig.timeout,
    })

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status
        const errorData = error.response?.data
        const errorType = errorData?.error

        // Handle JWT token expiry or authorization failures
        // 401 = Unauthorized (missing/invalid token)
        // 403 = Access Denied (expired token or insufficient permissions)
        if (status === 401 || status === 403) {
          // Check if this is an auth-related error (not a permission error for a specific resource)
          // Our custom gateway responses set error: "unauthorized" or "session_expired"
          const isAuthError = errorType === 'unauthorized' ||
                            errorType === 'session_expired' ||
                            !errorType // Fallback for any 401/403 without explicit error type

          if (isAuthError) {
            // First try to renew the session before giving up. The access token
            // lives 24h; without this the user is bounced to Google daily.
            const original = error.config as (typeof error.config & { _tnxRetried?: boolean })
            const isRenewalCall = typeof original?.url === 'string' && original.url.includes('/auth/refresh')

            if (original && !original._tnxRetried && !isRenewalCall) {
              original._tnxRetried = true // one attempt per request, never a loop

              // Imported lazily: auth.ts imports this module, so a static
              // import would be a cycle.
              const { authService } = await import('./auth')
              const renewed = await authService.refreshSession()

              if (renewed) {
                original.headers = original.headers || {}
                original.headers['Authorization'] = `Bearer ${localStorage.getItem('tnx_access_token')}`
                return this.client(original)
              }
            }

            console.warn('Authentication failed - session expired or invalid token')

            // Clear authentication data
            localStorage.removeItem('tnx_access_token')
            localStorage.removeItem('tnx_refresh_token')
            localStorage.removeItem('tnx_user_info')
            this.clearAuthToken()

            // Redirect to login page
            if (typeof window !== 'undefined') {
              window.location.href = '/'
            }
          }
          return Promise.reject(error)
        }

        if (status >= 500) {
          console.error('Server error:', error.response?.data)
        }
        return Promise.reject(error)
      }
    )

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add JWT Bearer token to requests
        try {
          const accessToken = localStorage.getItem('tnx_access_token')
          if (accessToken) {
            config.headers = config.headers || {}
            config.headers['Authorization'] = `Bearer ${accessToken}`
          }
        } catch (error) {
          console.warn('Failed to add JWT token to request:', error)
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  async get<T = any>(url: string, params?: Record<string, any>): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, { params })
    return response.data
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data)
    return response.data
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data)
    return response.data
  }

  async delete<T = any>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url)
    return response.data
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data)
    return response.data
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  /**
   * Clear authentication token
   */
  clearAuthToken(): void {
    delete this.client.defaults.headers.common['Authorization']
  }

  /**
   * Update base URL
   */
  updateBaseURL(newBaseURL: string): void {
    this.client.defaults.baseURL = newBaseURL
  }
}

// Create singleton instance
export const apiClient = new ApiClient()

// Typed API response helpers
export const handleApiResponse = <T>(response: ApiResponse<T>): T => {
  if (!response.success) {
    throw new Error(response.error || 'API request failed')
  }
  return response.data as T
}

export const handlePaginatedResponse = <T>(
  response: PaginatedResponse<T>
): PaginatedResponse<T> => {
  if (!response.success) {
    throw new Error('API request failed')
  }
  return response
}
